/**
 * Iter 69 — Admin FX overrides panel.
 *
 * Lists every supported display currency side-by-side with:
 *   • Live rate from frankfurter / fallback
 *   • Admin override (editable, blank means "use live")
 *   • Effective rate (what the platform actually serves)
 *
 * Admin actions:
 *   • Save — PUT the merged overrides
 *   • Reset — drop a single override (X button per row)
 *   • Clear all — wipe the override doc
 *   • Refresh — bust the in-memory FX cache so the next read is fresh
 *
 * Read-only base currency (EUR) is hidden — it's always 1.0 by definition.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { RefreshCcw, Save, RotateCcw, X, Coins } from "lucide-react";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString(undefined, { maximumFractionDigits: 6 }) : "—");

export default function AdminFxPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});  // {CCY: "1.10" | ""}
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fx/overrides");
      setData(data);
      // Pre-populate draft inputs with current override values so the admin
      // can adjust an existing pin without re-typing it.
      const next = {};
      for (const [k, v] of Object.entries(data.admin_overrides || {})) next[k] = String(v);
      setDrafts(next);
    } catch (e) {
      toast.error("Could not load FX overrides", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaveAll = async () => {
    setSaving(true);
    try {
      const rates = {};
      for (const [k, v] of Object.entries(drafts)) {
        const num = Number(v);
        if (Number.isFinite(num) && num > 0) rates[k] = num;
      }
      const { data } = await api.put("/admin/fx/overrides", { rates });
      setData(data);
      toast.success("FX overrides saved", { description: `${Object.keys(rates).length} currencies pinned` });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const onClearOne = async (ccy) => {
    try {
      const { data } = await api.delete(`/admin/fx/overrides/${ccy}`);
      setData(data);
      setDrafts((d) => { const n = { ...d }; delete n[ccy]; return n; });
      toast.success(`${ccy} override cleared`);
    } catch (e) {
      toast.error("Clear failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const onClearAll = async () => {
    if (!window.confirm("Wipe every admin FX override? Live frankfurter rates will resume immediately.")) return;
    try {
      const { data } = await api.put("/admin/fx/overrides", { clear: true });
      setData(data);
      setDrafts({});
      toast.success("All FX overrides cleared");
    } catch (e) {
      toast.error("Clear failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/fx/refresh");
      await load();
      toast.success("FX cache refreshed");
    } catch (e) {
      toast.error("Refresh failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="rounded-2xl border border-border bg-white p-6 text-sm text-neutral-500" data-testid="admin-fx-loading">
        Loading FX overrides…
      </div>
    );
  }

  const currencies = (data.supported || []).filter((c) => c !== "EUR");
  const overrideCount = Object.keys(data.admin_overrides || {}).length;

  return (
    <div className="rounded-2xl border border-border bg-white p-6" data-testid="admin-fx-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
        <div className="min-w-0">
          <h3 className="font-display text-xl font-semibold flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-600" />
            FX rate overrides
          </h3>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
            Pin any display currency to a custom rate. Useful when the ECB feed doesn't list a market (e.g. ALL) or you need an emergency adjustment.
            Pricing & payments remain in EUR — overrides only affect how prices are <em>shown</em> to renters.
          </p>
          <div className="text-xs text-neutral-400 mt-1.5">
            Live source: <strong className="text-neutral-600">{data.live_source}</strong> · Updated <strong className="text-neutral-600">{data.live_fetched_at}</strong>
            {data.updated_at && (
              <> · Overrides last edited <strong className="text-neutral-600">{new Date(data.updated_at).toLocaleString()}</strong>{data.updated_by ? ` by ${data.updated_by}` : ""}</>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="admin-fx-refresh-btn"
          >
            <RefreshCcw className={`h-4 w-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh feed
          </Button>
          {overrideCount > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClearAll}
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              data-testid="admin-fx-clear-all-btn"
            >
              <RotateCcw className="h-4 w-4 mr-1.5" />
              Clear all
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-neutral-400 border-b border-neutral-100">
              <th className="px-2 py-2 font-semibold">Currency</th>
              <th className="px-2 py-2 font-semibold">Live rate (1 EUR =)</th>
              <th className="px-2 py-2 font-semibold">Admin override</th>
              <th className="px-2 py-2 font-semibold">Effective</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {currencies.map((ccy) => {
              const override = data.admin_overrides?.[ccy];
              const effective = data.effective_rates?.[ccy];
              // The "live" number is whatever the source delivered before
              // the override layer. We don't have it explicitly when an
              // override is set, so when no override is set, live === effective.
              const live = override === undefined ? effective : undefined;
              return (
                <tr key={ccy} className="border-b border-neutral-100 last:border-b-0" data-testid={`admin-fx-row-${ccy}`}>
                  <td className="px-2 py-2.5 font-bold text-neutral-800">{ccy}</td>
                  <td className="px-2 py-2.5 text-neutral-600 tabular-nums">{live !== undefined ? fmt(live) : <span className="text-neutral-400">—</span>}</td>
                  <td className="px-2 py-2.5">
                    <Input
                      type="number"
                      step="0.000001"
                      min="0"
                      inputMode="decimal"
                      placeholder="Use live"
                      value={drafts[ccy] ?? ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [ccy]: e.target.value }))}
                      className="h-8 w-32 text-sm tabular-nums"
                      data-testid={`admin-fx-input-${ccy}`}
                    />
                  </td>
                  <td className="px-2 py-2.5 tabular-nums font-semibold text-neutral-900">{fmt(effective)}</td>
                  <td className="px-2 py-2.5 text-right">
                    {override !== undefined && (
                      <button
                        type="button"
                        onClick={() => onClearOne(ccy)}
                        title={`Clear ${ccy} override`}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full text-neutral-400 hover:text-rose-700 hover:bg-rose-50 transition"
                        data-testid={`admin-fx-clear-${ccy}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <Button
          type="button"
          onClick={onSaveAll}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="admin-fx-save-btn"
        >
          <Save className="h-4 w-4 mr-1.5" />
          {saving ? "Saving…" : "Save overrides"}
        </Button>
      </div>
    </div>
  );
}
